// @ts-nocheck

import { useState } from "react";
import { useConfirm } from "@/providers/ConfirmProvider";
import { cn } from "@/lib/utils";
import UserDisplay from "@/components/shared/UserDisplay";
import PrintDialog from "@/components/shared/PrintDialog";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { EntityLink } from "@/components/shared/EntityLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  Printer,
  Package,
  ShoppingCart,
  FileText,
  History,
  Calculator,
  Paperclip,
  CheckCircle2,
  MessageSquare,
} from "lucide-react";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CostEstimateSpreadsheet } from "@/components/sales/quotations/CostEstimateSpreadsheet";
import { UpdatePriceDialog } from "@/components/sales/quotations/UpdatePriceDialog";
import { QuotationEstimatingModal } from "./QuotationEstimatingModal";
import { ActionGuard } from "@/components/shared/ActionGuard";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, formatDate, formatNumber } from "@/lib/utils";
import { getStatusConfig } from "@/config/status-registry";
import WorkflowStepper from "@/components/shared/WorkflowStepper";
import { useWorkflowSteps } from "@/hooks/useWorkflowSteps";
import { useAuthStore } from "@/stores/auth.store";
import { ConfigLabel } from "@/components/shared/ConfigLabel";
import { formatProductSpecs } from "@/lib/format-specs";

const formatSpecs = formatProductSpecs;
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import RelatedDataTabs from "@/components/shared/RelatedDataTabs";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { DetailField as F } from "@/components/shared/DetailField";
import { orderLineDetailText } from "@/lib/order-detail-text";

const fmt = (v: number | null | undefined, curr = "VND") => {
  if (v == null) return "—";
  return curr === "VND" || !curr ? `${formatMoney(v)}đ` : `${formatMoney(v)} ${curr}`;
};
const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";


// ── KPI Stat (compact) ────────────────────────────────────────
const Stat = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white border text-sm">
    <span className="text-slate-700 text-xs">{label}</span>
    <span className={`font-bold tabular-nums ${color || "text-slate-900"}`}>
      {value}
    </span>
  </div>
);

export default function QuotationViewPage({
  quotationId: propQuotationId,
  isZIndex = false,
}: {
  quotationId?: string;
  isZIndex?: boolean;
} = {}) {
  const params = useParams({ strict: false }) as any;
  const quotationId = propQuotationId || params?.quotationId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subTab, setSubTab] = useState("details");
  const [showPrint, setShowPrint] = useState(false);
  const [showCreateOrderDialog, setShowCreateOrderDialog] = useState(false);
  const [selectedDetailIds, setSelectedDetailIds] = useState<string[]>([]);
  const { workflow } = useWorkflowSteps("quotation");

  // State for Technical Estimating Modal
  const [estimatingDetailId, setEstimatingDetailId] = useState<string | null>(null);
  const [estimatingProductName, setEstimatingProductName] = useState<string>('');
  const [estimatingOrderQty, setEstimatingOrderQty] = useState<number>(1000);
  const [estimatingSpecs, setEstimatingSpecs] = useState<string>('');

  // ── Main data (includes: customer, details with product) ──
  const {
    data: quotationData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["quotation", quotationId],
    queryFn: async () =>
      trpcClient.quotations.getById.query({ id: quotationId }),
    staleTime: 0,
    refetchOnMount: "always",
  });

  // ── Related orders (FK: quotationId on Order model) ──
  const { data: relatedOrders = [] } = useQuery({
    queryKey: ["orders-by-quotation", quotationId],
    queryFn: async () => {
      try {
        const q = quotationData as any;
        const res = await trpcClient.orders.list.query({
          quotationId: q?.id || quotationId,
          limit: 100,
        });
        return res.items || [];
      } catch {
        return [];
      }
    },
    enabled: !!quotationId,
  });

  const q = quotationData as any;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.quotations.delete.mutate({ id: quotationId });

      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      navigate({ to: "/quotations" });
    } catch (err: any) {
      toast.error(err?.message || "Xoá thất bại");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    // Data Completeness Guard: Chặn gửi hoặc duyệt nếu có sản phẩm đơn giá <= 0đ hoặc chưa có mặt hàng
    if (newStatus === "SENT" || newStatus === "APPROVED") {
      const detailsList = q?.details || [];
      if (detailsList.length === 0) {
        toast.error("Báo giá chưa có mặt hàng nào. Không thể gửi hoặc duyệt!");
        return;
      }
      const invalidItems = detailsList.filter(
        (d: any) => (Number(d.quotedPrice) || Number(d.priceVND) || 0) <= 0,
      );
      if (invalidItems.length > 0) {
        const itemNames = invalidItems
          .map((d: any) => d.productName || d.product?.name || "Sản phẩm")
          .join(", ");
        toast.error(
          `Không thể ${newStatus === "SENT" ? "gửi báo giá" : "chuyển khách duyệt"}: Các sản phẩm sau chưa có đơn giá hợp lệ (> 0đ): ${itemNames}`,
        );
        return;
      }
    }

    if (statusUpdating) return;

    // Xác nhận trước khi gửi báo giá cho khách hàng / chuyển khách duyệt để tránh thao tác nhầm
    if (newStatus === "SENT" || newStatus === "APPROVED") {
      const ok = await confirm({
        title: newStatus === "SENT" ? "Xác nhận gửi báo giá" : "Xác nhận khách duyệt",
        message:
          newStatus === "SENT"
            ? `Gửi báo giá ${q?.quotationId || ""} cho khách hàng? Trạng thái sẽ chuyển sang "Đã gửi".`
            : `Xác nhận khách hàng đã duyệt báo giá ${q?.quotationId || ""}?`,
        confirmText: newStatus === "SENT" ? "Gửi báo giá" : "Xác nhận duyệt",
      });
      if (!ok) return;
    }

    setStatusUpdating(true);
    try {
      await trpcClient.quotations.updateStatus.mutate({
        id: quotationId,
        status: newStatus as any,
      });
      toast.success(
        `Đã chuyển báo giá sang trạng thái "${getStatusConfig("QuotationStatus", newStatus)?.label || newStatus}"`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["quotation", quotationId],
      });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });
      await queryClient.invalidateQueries({ queryKey: ["quotations"] });
      await queryClient.invalidateQueries({
        queryKey: ["activity-log", "quotation", q?.id || quotationId],
      });
    } catch (err: any) {
      toast.error(err?.message || "Cập nhật trạng thái thất bại");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleCalculateEstimate = async (detailId: string) => {
    try {
      toast.loading("Đang tính giá...", { id: "calc" });
      await trpcClient.quotations.calculateCostEstimate.mutate({
        detailId,
        batchSizeSheets: 1000,
      });
      await queryClient.invalidateQueries({
        queryKey: ["quotation", quotationId],
      });
      toast.success("Tính giá chi tiết thành công", { id: "calc" });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi tính giá", { id: "calc" });
    }
  };

  if (isLoading) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Báo giá", to: "/quotations" }]} />;
  }

  if (error || !q) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Báo giá", to: "/quotations" }]}
        title="Không tìm thấy"
      >
        <div className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <Button onClick={() => navigate({ to: "/quotations" })}>
            Quay lại
          </Button>
        </div>
      </StandardDetailLayout>
    );
  }

  // ── Computed from REAL schema fields ──
  const statusCfg = getStatusConfig("QuotationStatus", q.status);
  const details: any[] = q.details || [];
  const getEffectivePrice = (d: any) => Number(d.quotedPrice) || Number(d.priceVND) || 0;
  // QuotationDetail: quantity, priceVND, priceUSD, costPrice, profitMargin
  const lineTotal = details.reduce(
    (s: number, d: any) =>
      s + Number(d.quantity || 0) * getEffectivePrice(d),
    0,
  );
  const shippingFee = Number(q.shippingFee || 0);
  const grandTotal = lineTotal + shippingFee;

  const ordersList = Array.isArray(relatedOrders) ? relatedOrders : [];

  return (
    <>
      <StandardDetailLayout
        breadcrumbs={[{ label: "Báo giá", to: "/quotations" }]}
        title={q.quotationId}
        entityId={q.id}
        entityType="quotation"
        isZIndex={isZIndex}
        timestamps={{
          created: q.createdAt,
          updated: q.updatedAt,
          createdBy: q.creator?.fullName,
          updatedBy: q.approver?.fullName || q.creator?.fullName,
        }}
        status={
          <div className="flex items-center gap-1.5 flex-wrap">
            {q.isNonMoq && (
              <Badge
                variant="default"
                className="text-xs bg-purple-600 hover:bg-purple-700 text-white border-none shadow-xs"
              >
                NON-MOQ
              </Badge>
            )}
            {q.version > 1 && (
              <Badge variant="outline" className="text-xs font-mono">
                v{q.version}
              </Badge>
            )}
            <Badge variant="outline" className={cn("text-xs font-medium", statusCfg.color)}>
              {statusCfg.label || q.status}
            </Badge>
          </div>
        }
        actions={
          <div className="grid grid-cols-4 gap-1.5 w-full sm:flex sm:items-center sm:gap-1.5 sm:w-auto">
            {/* ── Workflow actions ── */}
            {/* Đang xử lý = đã Lưu, chờ tính giá; giá bán do sale nhập nên vẫn Gửi BG được (FB-NCF9YD) */}
            {(q.status === "DRAFT" || q.status === "PROCESSING") && (
              <>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-1.5 sm:px-3 text-[11px] sm:text-xs font-medium justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap"
                  disabled={statusUpdating}
                  onClick={() => handleStatusChange("SENT")}
                  data-testid="quotation-send-btn"
                >
                  Gửi BG
                </Button>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white h-8 px-1.5 sm:px-3 text-[11px] sm:text-xs font-medium justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap"
                  disabled={statusUpdating}
                  onClick={() => handleStatusChange("ESTIMATED")}
                >
                  Tính giá
                </Button>
              </>
            )}
            {q.status === "SENT" && (
              <>
                <ActionGuard
                  module="quotations"
                  action="approve"
                  fallbackMessage="Chỉ Giám Đốc hoặc Trưởng phòng Kinh Doanh mới có quyền duyệt Báo Giá này"
                >
                  <Button
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white h-8 px-1.5 sm:px-3 text-[11px] sm:text-xs font-medium justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap"
                    disabled={statusUpdating}
                    onClick={() => handleStatusChange("APPROVED")}
                  >
                    Khách duyệt
                  </Button>
                </ActionGuard>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white h-8 px-1.5 sm:px-3 text-[11px] sm:text-xs font-medium justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap"
                  disabled={statusUpdating}
                  onClick={() => handleStatusChange("ESTIMATED")}
                >
                  Tính giá
                </Button>
              </>
            )}
            {q.status === "ESTIMATED" && (
              <>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-1.5 sm:px-3 text-[11px] sm:text-xs font-medium justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap"
                  disabled={statusUpdating}
                  onClick={() => handleStatusChange("SENT")}
                >
                  Gửi BG
                </Button>
                <ActionGuard
                  module="quotations"
                  action="approve"
                  fallbackMessage="Chỉ Giám Đốc hoặc Trưởng phòng Kinh Doanh mới có quyền duyệt Báo Giá này"
                >
                  <Button
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white h-8 px-1.5 sm:px-3 text-[11px] sm:text-xs font-medium justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap"
                    disabled={statusUpdating}
                    onClick={() => handleStatusChange("APPROVED")}
                  >
                    Khách duyệt
                  </Button>
                </ActionGuard>
              </>
            )}
            {q.status === "APPROVED" && (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-2 sm:px-3 text-[11px] sm:text-xs font-semibold justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap shadow-sm"
                onClick={() => {
                  setSelectedDetailIds(details.map((d: any) => d.id));
                  setShowCreateOrderDialog(true);
                }}
              >
                <ShoppingCart className="mr-1.5 h-3.5 w-3.5 shrink-0" /> Lập Đơn Hàng
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-1.5 sm:px-3 text-[11px] sm:text-xs font-medium justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap"
              onClick={() => setShowPrint(true)}
            >
              <Printer className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> In BG
            </Button>
            <Button
              variant="default"
              size="sm"
              className="h-8 px-1.5 sm:px-3 text-[11px] sm:text-xs font-medium justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap"
              onClick={() =>
                navigate({
                  to: "/quotations/$quotationId/edit",
                  params: { quotationId },
                })
              }
            >
              <Pencil className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> Sửa
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50 h-8 px-1.5 sm:px-3 text-[11px] sm:text-xs font-medium justify-center sm:justify-start w-full sm:w-auto whitespace-nowrap"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> Xoá
            </Button>
          </div>
        }
        workflow={
          <WorkflowStepper
            workflow={workflow}
            currentStatus={q.status}
            onStatusChange={handleStatusChange}
          />
        }
      >
        <ConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title={`Xoá báo giá ${q.quotationId}?`}
          description="Hành động này không thể hoàn tác."
          confirmText="Xoá báo giá"
          onConfirm={handleDelete}
          isLoading={isDeleting}
          variant="destructive"
        />
        {/* ═══ 1. THÔNG TIN CHUNG (Dense 4-Column Grid) ═══ */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2.5">
            {/* Row 1: Khách hàng (2 cols) + Mã KH (1 col) + Hiệu lực đến (1 col) */}
            <div className="col-span-2">
              <F
                label="Khách hàng"
                value={
                  q.customerId ? (
                    <EntityLink
                      entityType="customer"
                      id={q.customerId}
                      label={q.customer?.shortName || q.customer?.name}
                      search={{ returnUrl: `/quotations/${quotationId}` }}
                    />
                  ) : (
                    q.customer?.shortName || q.customer?.name || "—"
                  )
                }
                hl
              />
            </div>
            <F label="Mã KH" value={q.customer?.code} mono />
            <F label="Hiệu lực đến" value={q.validUntil ? <span className="whitespace-nowrap">{fmtD(q.validUntil)}</span> : undefined} />

            {/* Row 2: Người liên hệ (1 col) + Điện thoại (1 col) + Email (1 col) + NV Kinh doanh (1 col) */}
            <F label="Liên hệ" value={q.contactPerson ? <span className="break-words whitespace-normal">{q.contactPerson}</span> : undefined} />
            <F label="Điện thoại" value={q.phone ? <span className="whitespace-nowrap font-mono">{q.phone}</span> : undefined} />
            <F label="Email" value={q.email ? <span className="break-words whitespace-normal block" title={q.email}>{q.email}</span> : undefined} />
            <F
              label="NV Kinh doanh"
              value={
                q.creator?.fullName ? (
                  <UserDisplay
                    name={q.creator?.fullName}
                    avatar={q.creator?.avatar}
                  />
                ) : "—"
              }
            />

            {/* Row 3: Địa chỉ giao hàng merge 3 cột rộng rãi (3 cols) + Người duyệt / Ngày duyệt (1 col) */}
            <div className="col-span-2 lg:col-span-3">
              <F label="Địa chỉ giao" value={q.deliveryAddress} />
            </div>
            <div>
              {q.approver ? (
                <F
                  label="Người duyệt"
                  value={
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <UserDisplay
                        name={q.approver?.fullName}
                        avatar={q.approver?.avatar}
                      />
                      {q.approvedDate && (
                        <span className="text-[11px] text-slate-400 font-normal whitespace-nowrap">
                          ({fmtD(q.approvedDate)})
                        </span>
                      )}
                    </div>
                  }
                />
              ) : (
                <F label="Người duyệt" value="—" />
              )}
            </div>

            {/* Row 4: Điều khoản thương mại — thẳng hàng các cột bên trên */}
            <F label="Điều kiện TT" value={q.paymentTerm} />
            <F label="Đặt cọc" value={q.deposit} />
            <F label="TG giao hàng" value={q.deliveryTime} />
            <F
              label="Ngoại tệ"
              value={
                q.currency && q.currency !== "VND"
                  ? `${q.currency} (${Number(q.exchangeRate).toLocaleString("vi-VN")})`
                  : "VNĐ"
              }
            />
          </div>

          {/* Khối Ghi chú chuyên biệt */}
          <div className="mt-2.5 pt-2.5 border-t border-slate-100">
            <div className="flex items-start gap-2">
              <span className="text-slate-500 text-xs font-medium shrink-0 pt-0.5 w-16">
                Ghi chú:
              </span>
              <div className="flex-1 bg-slate-50/80 border border-slate-200/80 rounded px-2.5 py-1.5 text-xs text-slate-700 whitespace-pre-wrap leading-relaxed min-h-[32px]">
                {q.notes ? (
                  q.notes
                ) : (
                  <span className="text-slate-400 italic">Không có ghi chú</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ═══ 2. TABS: Sản phẩm | Đơn hàng ═══ */}
        <div className="mt-2">
          <RelatedDataTabs
            defaultTab={subTab}
            tabs={[
              {
                id: "details",
                label: "Sản phẩm",
                count: details.length,
                icon: <Package className="h-3.5 w-3.5 mr-1.5" />,
                content: (
                  <div className="bg-white border border-slate-200 rounded-b-lg shadow-sm p-0 border-t border-slate-100">
                    {/* ── QuotationDetail fields: productName, productCode, specs, unit, quantity, priceVND, profitMargin ── */}
                    {details.length === 0 ? (
                      <div className="text-center text-sm text-slate-600 py-6">
                        Chưa có chi tiết sản phẩm
                      </div>
                    ) : (
                      (() => {
                        const normalized = details.map((d: any) => ({
                          ...d,
                          _sortDesc: (d.tags || d.product?.tags || "")
                            .toLowerCase()
                            .trim(),
                          // Sản phẩm khác mã nhưng trùng tên không được gộp chung ô (FB-9BRTHU)
                          _sortProduct: d.productId || d.product?.id || d.productCode || d.product?.productCode || '',
                          _sortName: (d.productName || d.productCode || "")
                            .toLowerCase()
                            .trim(),
                          _sortSpec: formatSpecs(d.specs, d.product),
                          // Nội dung ô "Chi tiết": chỉ gộp ô các dòng liền nhau có chi tiết giống hệt (không gộp theo tags)
                          _detail: orderLineDetailText(d),
                        }));

                        const sorted = normalized.sort((a: any, b: any) => {
                          if (a._sortDesc !== b._sortDesc)
                            return a._sortDesc.localeCompare(b._sortDesc);
                          if (a._sortName !== b._sortName)
                            return a._sortName.localeCompare(b._sortName);
                          return a._sortSpec.localeCompare(b._sortSpec);
                        });

                        const spanDetails: number[] = new Array(
                          sorted.length,
                        ).fill(0);
                        let gi = 0;
                        while (gi < sorted.length) {
                          let gj = gi + 1;
                          while (
                            gj < sorted.length &&
                            sorted[gj]._sortDesc === sorted[gi]._sortDesc &&
                            sorted[gj]._detail === sorted[gi]._detail
                          )
                            gj++;
                          spanDetails[gi] = gj - gi;
                          gi = gj;
                        }

                        const spanProduct: number[] = new Array(
                          sorted.length,
                        ).fill(0);
                        gi = 0;
                        while (gi < sorted.length) {
                          let gj = gi + 1;
                          while (
                            gj < sorted.length &&
                            sorted[gj]._sortDesc === sorted[gi]._sortDesc &&
                            sorted[gj]._sortName === sorted[gi]._sortName &&
                            sorted[gj]._sortProduct === sorted[gi]._sortProduct &&
                            sorted[gj]._sortSpec === sorted[gi]._sortSpec &&
                            sorted[gj]._detail === sorted[gi]._detail
                          )
                            gj++;
                          spanProduct[gi] = gj - gi;
                          gi = gj;
                        }

                        return (
                          <>
                            <div className="hidden md:block overflow-x-auto">
                              <Table className="table-fixed min-w-[1200px]">
                                <TableHeader>
                                  <TableRow className="text-[11px]">
                                    <TableHead
                                      style={{ width: "4%" }}
                                      className="text-center"
                                    >
                                      STT
                                    </TableHead>
                                    <TableHead style={{ width: "22%" }}>
                                      Sản phẩm
                                    </TableHead>
                                    <TableHead style={{ width: "18%" }}>
                                      Chi tiết
                                    </TableHead>
                                    <TableHead className="w-12 text-center px-3">
                                      ĐVT
                                    </TableHead>
                                    <TableHead className="text-right w-16 px-3">
                                      SL
                                    </TableHead>
                                    <TableHead className="text-right w-24 px-3">
                                      Giá USD
                                    </TableHead>
                                    <TableHead className="text-right w-28 px-3">
                                      Giá VND
                                    </TableHead>
                                    <TableHead className="text-right w-32 px-3">
                                      Thành tiền
                                    </TableHead>
                                    <TableHead className="text-right w-24 px-3">
                                      Giá vốn
                                    </TableHead>
                                    <TableHead className="text-right w-16 px-3">
                                      Margin
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {sorted.map((d: any, idx: number) => {
                                    const priceUSD = Number(d.priceUSD) || 0;
                                    const costPrice = Number(d.costPrice) || 0;
                                    const specsText = formatSpecs(
                                      d.specs || d.product?.specs,
                                      d.product,
                                    );
                                    // Chất liệu trước, rồi các công đoạn — cùng cách hiển thị với Đơn hàng
                                    const processText: string = d._detail;

                                    const showMergeProduct =
                                      spanProduct[idx] > 0;
                                    const showMergeDetails =
                                      spanDetails[idx] > 0;

                                    return (
                                      <TableRow key={d.id || idx}>
                                        <TableCell className="text-center text-slate-600 py-2 align-middle">
                                          {idx + 1}
                                        </TableCell>

                                        {showMergeProduct && (
                                          <TableCell
                                            rowSpan={spanProduct[idx]}
                                            className="align-middle overflow-hidden py-2 px-2"
                                          >
                                            <div className="flex items-center gap-2.5">
                                              {d.product?.image ? (
                                                <ImageLightbox src={d.product.image} alt={d.productName || "HÌNH ẢNH SP"}>
                                                  <img
                                                    src={d.product.image}
                                                    className="w-[34px] h-[34px] object-contain rounded border border-slate-200 shrink-0 cursor-pointer hover:opacity-80 transition-opacity bg-white"
                                                    alt="HÌNH ẢNH SP"
                                                  />
                                                </ImageLightbox>
                                              ) : (
                                                <div className="w-[34px] h-[34px] rounded border border-slate-200 bg-white flex items-center justify-center shrink-0">
                                                  <Package className="w-4 h-4 text-slate-300" />
                                                </div>
                                              )}
                                              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                                <div className="flex items-start gap-2 max-w-full">
                                                  {d.productGroup && (
                                                    <Badge
                                                      variant="outline"
                                                      className="text-[10px] font-medium uppercase px-1.5 py-0 min-h-0 bg-slate-100 text-slate-700 whitespace-nowrap shrink-0 mt-0.5"
                                                    >
                                                      {d.productGroup}
                                                    </Badge>
                                                  )}
                                                  <div className="flex flex-col gap-0 w-full min-w-0">
                                                    {(d.productCode ||
                                                      d.product
                                                        ?.productCode) && (
                                                      <div
                                                        className="text-[11px] text-slate-600 whitespace-normal break-words"
                                                        title={
                                                          d.productCode ||
                                                          d.product?.productCode
                                                        }
                                                      >
                                                        {d.productCode ||
                                                          d.product
                                                            ?.productCode}
                                                      </div>
                                                    )}
                                                    {d.productId ? (
                                                      <EntityLink
                                                        entityType="product"
                                                        id={d.productId}
                                                        label={d.productName || "—"}
                                                        search={{ returnUrl: `/quotations/${quotationId}` }}
                                                        className="font-bold text-xs"
                                                        title={d.productName}
                                                      />
                                                    ) : (
                                                      <div
                                                        className="font-bold text-xs text-slate-900 break-words"
                                                        title={d.productName}
                                                      >
                                                        {d.productName || "—"}
                                                      </div>
                                                    )}
                                                    {specsText && (
                                                      <div
                                                        className="text-[11px] text-slate-700 italic whitespace-normal break-words mt-0.5"
                                                        title={specsText}
                                                      >
                                                        {specsText}
                                                      </div>
                                                    )}
                                                    {d.notes && (
                                                      <div className="text-[11px] text-amber-800 mt-0.5 whitespace-pre-wrap break-words" data-testid="quotation-line-note">
                                                        Ghi chú: {d.notes}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </TableCell>
                                        )}

                                        {showMergeDetails && (
                                          <TableCell
                                            rowSpan={spanDetails[idx]}
                                            className="text-[11px] whitespace-pre-wrap align-middle py-2 border-l border-slate-100 px-3"
                                            data-testid={`line-detail-${d.id}`}
                                          >
                                            {processText || (
                                              <span className="text-slate-300">
                                                —
                                              </span>
                                            )}
                                          </TableCell>
                                        )}

                                        {showMergeProduct && (
                                          <TableCell
                                            rowSpan={spanProduct[idx]}
                                            className="text-center text-[11px] align-middle py-2 px-2"
                                          >
                                            {d.unit || "PCS"}
                                          </TableCell>
                                        )}
                                        <TableCell className="text-right tabular-nums py-2 px-2 align-middle">
                                          {formatNumber(d.quantity)}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums text-[11px] py-2 px-2 align-middle">
                                          {priceUSD > 0
                                            ? `$${priceUSD.toFixed(3)}`
                                            : "—"}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums py-2 px-2 align-middle">
                                          {d.quotedPrice && Number(d.quotedPrice) !== Number(d.priceVND) ? (
                                            <div className="flex flex-col items-end">
                                              <span className="font-semibold text-emerald-700">{fmt(d.quotedPrice)}</span>
                                              <span className="text-[10px] text-slate-400 line-through">Catalog: {fmt(d.priceVND)}</span>
                                            </div>
                                          ) : (
                                            <span>{fmt(d.priceVND)}</span>
                                          )}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums font-semibold py-2 px-2 align-middle text-emerald-800">
                                          {fmt(
                                            Number(d.quantity) *
                                              getEffectivePrice(d),
                                          )}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums text-[11px] text-slate-700 py-2 px-2 align-middle">
                                          <div className="flex flex-col items-end gap-1">
                                            <span>
                                              {costPrice > 0
                                                ? fmt(costPrice)
                                                : "—"}
                                            </span>
                                            {(d.pricingStatus === "PENDING" ||
                                              !d.pricingStatus) && (
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-6 px-2 text-[10px] text-indigo-700 bg-indigo-50/60 border-indigo-200 hover:bg-indigo-100 font-bold"
                                                onClick={() => {
                                                  setEstimatingDetailId(d.id);
                                                  setEstimatingProductName(d.productName || d.product?.productName || '');
                                                  setEstimatingOrderQty(Number(d.quantity) || 1000);
                                                  setEstimatingSpecs(typeof d.specs === 'string' ? d.specs : formatSpecs(d.specs, d.product));
                                                }}
                                              >
                                                <Calculator className="w-3 h-3 mr-1" />
                                                Tính Giá
                                              </Button>
                                            )}
                                            {d.pricingStatus === "ESTIMATED" && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 px-2 text-[10px] bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-200 rounded font-bold cursor-pointer shadow-2xs gap-1"
                                                onClick={() => {
                                                  setEstimatingDetailId(d.id);
                                                  setEstimatingProductName(d.productName || d.product?.productName || '');
                                                  setEstimatingOrderQty(Number(d.quantity) || 1000);
                                                  setEstimatingSpecs(typeof d.specs === 'string' ? d.specs : formatSpecs(d.specs, d.product));
                                                }}
                                                title="Bấm để xem lại hoặc điều chỉnh bóc tách dự toán kỹ thuật (BM-KT-01)"
                                              >
                                                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                                BM-KT-01 ({fmt(costPrice)})
                                              </Button>
                                            )}
                                            {d.pricingStatus === "ESTIMATING" && (
                                              <Badge
                                                variant="outline"
                                                className="text-[9px] bg-amber-50 text-amber-600 border-amber-200"
                                              >
                                                Đang tính...
                                              </Badge>
                                            )}
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums text-emerald-600 py-2 px-2 align-middle">
                                          <div className="flex items-center justify-end gap-2">
                                            <span>
                                              {d.profitMargin != null
                                                ? `${Number(d.profitMargin)}%`
                                                : "—"}
                                            </span>
                                            {q.status === "ESTIMATED" && (
                                              <UpdatePriceDialog detail={d} />
                                            )}
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                            <div className="md:hidden space-y-3 p-3 bg-slate-50/50">
                              {sorted.map((d: any, idx: number) => {
                                const specsText = formatSpecs(
                                  d.specs || d.product?.specs,
                                  d.product,
                                );
                                const priceUSD = Number(d.priceUSD) || 0;
                                const costPrice = Number(d.costPrice) || 0;

                                return (
                                  <div
                                    key={d.id || idx}
                                    className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                                  >
                                    <div className="p-3 border-b border-slate-100 flex gap-3 items-start relative">
                                      <div className="w-12 h-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0 overflow-hidden">
                                        {d.product?.image ? (
                                          <ImageLightbox src={d.product.image} alt={d.productName || "HÌNH ẢNH SP"}>
                                            <img
                                              src={d.product.image}
                                              className="w-full h-full object-contain cursor-pointer hover:opacity-80 transition-opacity"
                                              alt=""
                                            />
                                          </ImageLightbox>
                                        ) : (
                                          <Package className="w-5 h-5 text-slate-300" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0 pr-2">
                                        <div className="flex justify-between items-start">
                                          {d.productId ? (
                                            <EntityLink
                                              entityType="product"
                                              id={d.productId}
                                              label={d.productName || "—"}
                                              search={{ returnUrl: `/quotations/${quotationId}` }}
                                              className="font-bold text-[13px] break-words whitespace-normal"
                                            />
                                          ) : (
                                            <div className="font-bold text-[13px] text-slate-900 break-words whitespace-normal">
                                              {d.productName || "—"}
                                            </div>
                                          )}
                                        </div>
                                        {(d.productCode ||
                                          d.product?.productCode) && (
                                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                                            {d.productCode ||
                                              d.product?.productCode}
                                          </div>
                                        )}
                                        {specsText && (
                                          <div className="text-[11px] text-slate-700 italic mt-1 line-clamp-2">
                                            {specsText}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    <div className="p-3 bg-slate-50/30 flex flex-col gap-2">
                                      <div className="flex justify-between items-center text-[13px]">
                                        <span className="text-slate-500">
                                          Số lượng:
                                        </span>
                                        <span className="font-medium text-slate-900">
                                          {formatNumber(d.quantity)}{" "}
                                          <span className="text-[11px] text-slate-500 font-normal">
                                            {d.unit || "PCS"}
                                          </span>
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center text-[13px]">
                                        <span className="text-slate-500">
                                          Đơn giá:
                                        </span>
                                        <span className="font-medium text-slate-900">
                                          {d.quotedPrice && Number(d.quotedPrice) !== Number(d.priceVND) ? (
                                            <span className="text-emerald-700 font-semibold">{fmt(d.quotedPrice)}</span>
                                          ) : (
                                            <span>{fmt(d.priceVND)}</span>
                                          )}{" "}
                                          {priceUSD > 0 ? (
                                            <span className="text-[11px] text-slate-500">
                                              (${priceUSD.toFixed(3)})
                                            </span>
                                          ) : (
                                            ""
                                          )}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center text-[13px] font-medium border-t border-slate-100 pt-2">
                                        <span className="text-slate-700">
                                          Thành tiền:
                                        </span>
                                        <span className="text-emerald-700 font-semibold">
                                          {fmt(
                                            Number(d.quantity) *
                                              getEffectivePrice(d),
                                          )}
                                        </span>
                                      </div>
                                      {(costPrice > 0 ||
                                        d.profitMargin != null) && (
                                        <div className="flex justify-between items-center text-[11px] text-slate-500 pt-1">
                                          <span>
                                            Giá vốn:{" "}
                                            {costPrice > 0
                                              ? fmt(costPrice)
                                              : "—"}
                                          </span>
                                          <span>
                                            Margin:{" "}
                                            <span className="text-emerald-600 font-medium">
                                              {d.profitMargin != null
                                                ? `${Number(d.profitMargin)}%`
                                                : "—"}
                                            </span>
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()
                    )}
                  </div>
                ),
              },
              {
                id: "orders",
                label: "Đơn hàng",
                count: ordersList.length,
                icon: <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />,
                content: (
                  <div className="bg-white border border-slate-200 rounded-b-lg shadow-sm p-0 border-t border-slate-100">
                    {ordersList.length === 0 ? (
                      <div className="text-center text-sm text-slate-600 py-6">
                        Chưa có đơn hàng từ báo giá này
                      </div>
                    ) : (
                      <>
                        <div className="hidden md:block overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Mã ĐH</TableHead>
                                <TableHead>Ngày đặt</TableHead>
                                <TableHead className="text-right">
                                  Tổng tiền
                                </TableHead>
                                <TableHead>Trạng thái</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {ordersList.map((o: any) => {
                                const oCfg = getStatusConfig(
                                  "OrderStatus",
                                  o.status,
                                );
                                return (
                                  <TableRow
                                    key={o.id}
                                    className="cursor-pointer hover:bg-slate-50"
                                    onClick={() =>
                                      navigate({
                                        to: "/orders/$orderId",
                                        params: { orderId: o.id },
                                      })
                                    }
                                  >
                                    <TableCell className="font-mono text-xs font-medium">
                                      <EntityLink
                                        entityType="order"
                                        id={o.id}
                                        code={o.orderId}
                                        search={{ returnUrl: `/quotations/${quotationId}?tab=orders` }}
                                      />
                                    </TableCell>
                                    <TableCell>{fmtD(o.orderDate)}</TableCell>
                                    <TableCell className="text-right tabular-nums">
                                      {fmt(o.totalAmount)}
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        variant="outline"
                                        className={oCfg.color}
                                      >
                                        {oCfg.label || o.status}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                        <div className="md:hidden space-y-3 p-3 bg-slate-50/50">
                          {ordersList.map((o: any) => {
                            const oCfg = getStatusConfig(
                              "OrderStatus",
                              o.status,
                            );
                            return (
                              <div
                                key={o.id}
                                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden p-3 cursor-pointer active:scale-[0.99] transition-transform"
                                onClick={() =>
                                  navigate({
                                    to: "/orders/$orderId",
                                    params: { orderId: o.id },
                                  })
                                }
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <EntityLink
                                    entityType="order"
                                    id={o.id}
                                    code={o.orderId}
                                    search={{ returnUrl: `/quotations/${quotationId}?tab=orders` }}
                                    className="font-mono text-sm font-bold"
                                  />
                                  <Badge
                                    variant="outline"
                                    className={oCfg.color}
                                  >
                                    {oCfg.label || o.status}
                                  </Badge>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                  <div className="text-slate-500">
                                    {fmtD(o.orderDate)}
                                  </div>
                                  <div className="font-bold text-slate-900">
                                    {fmt(o.totalAmount)}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                ),
              },
              {
                id: "cost-estimate",
                label: "Bảng chiết giá",
                count: details.length,
                icon: <Calculator className="h-3.5 w-3.5 mr-1.5" />,
                content: (
                  <div className="bg-white border border-slate-200 rounded-b-lg shadow-sm p-4 border-t border-slate-100 space-y-8">
                    {details.length === 0 ? (
                      <div className="text-center text-sm text-slate-600 py-6">
                        Chưa có chi tiết sản phẩm để tính giá
                      </div>
                    ) : (
                      details.map((d: any, idx: number) => (
                        <div key={d.id} className="space-y-4">
                          <h4 className="font-bold text-md text-slate-800 border-b pb-2">
                            SP {idx + 1}:{" "}
                            {d.productName ||
                              d.product?.productName ||
                              "Sản phẩm không tên"}
                          </h4>
                          <CostEstimateSpreadsheet
                            quotationDetailId={d.id}
                            quotationId={q.id}
                            quantity={d.quantity}
                          />
                        </div>
                      ))
                    )}
                  </div>
                ),
              },
            ]}
          />
        </div>
      </StandardDetailLayout>
      <PrintDialog
        open={showPrint}
        onClose={() => setShowPrint(false)}
        type="quotation"
        data={q}
      />

      <Dialog
        open={showCreateOrderDialog}
        onOpenChange={setShowCreateOrderDialog}
      >
        <DialogContent className="max-w-4xl sm:max-w-4xl w-full">
          <DialogHeader>
            <DialogTitle>Chọn sản phẩm chốt đơn để chuyển sang Đơn hàng</DialogTitle>
          </DialogHeader>
          <div className="my-2 max-h-[50vh] overflow-y-auto border border-slate-200 rounded-md">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-slate-100 shadow-[inset_0_-1px_0_rgba(0,0,0,0.1)]">
                <TableRow>
                  <TableHead className="w-10 text-center">
                    <Checkbox
                      checked={
                        selectedDetailIds.length === details.length &&
                        details.length > 0
                      }
                      onCheckedChange={(c) =>
                        setSelectedDetailIds(
                          c ? details.map((d: any) => d.id) : [],
                        )
                      }
                    />
                  </TableHead>
                  <TableHead className="w-[25%]">Tên SP</TableHead>
                  <TableHead className="w-[30%]">Quy cách</TableHead>
                  <TableHead className="w-[15%] text-right">Số lượng</TableHead>
                  <TableHead className="w-[15%] text-right">Đơn giá</TableHead>
                  <TableHead className="w-[15%] text-right">Thành tiền</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {details.map((d: any) => {
                  const effectivePrice = getEffectivePrice(d);
                  const lineAmount = (Number(d.quantity) || 0) * effectivePrice;
                  const isChecked = selectedDetailIds.includes(d.id);
                  return (
                    <TableRow key={d.id} className={isChecked ? "bg-emerald-50/20" : ""}>
                      <TableCell className="align-middle text-center">
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={(c) => {
                            if (c)
                              setSelectedDetailIds([...selectedDetailIds, d.id]);
                            else
                              setSelectedDetailIds(
                                selectedDetailIds.filter((id) => id !== d.id),
                              );
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-medium text-xs whitespace-normal break-words align-middle">
                        {d.productName}
                      </TableCell>
                      <TableCell className="text-xs text-slate-700 whitespace-normal break-words align-middle">
                        {formatSpecs(d.specs || d.product?.specs, d.product) ||
                          "—"}
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono font-medium align-middle">
                        {formatNumber(d.quantity)} {d.unit || ""}
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono align-middle">
                        {d.quotedPrice && Number(d.quotedPrice) !== Number(d.priceVND) ? (
                          <span className="text-emerald-700 font-semibold">{formatMoney(effectivePrice)}</span>
                        ) : (
                          formatMoney(effectivePrice)
                        )}
                      </TableCell>
                      <TableCell className="text-right text-xs font-mono font-semibold text-emerald-800 align-middle">
                        {formatMoney(lineAmount)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between w-full">
            <div className="text-xs text-slate-600">
              Đã chọn: <span className="font-bold text-slate-900">{selectedDetailIds.length}/{details.length}</span> mặt hàng
              {" | "}Tổng tiền chốt: <span className="font-bold text-emerald-700 font-mono whitespace-nowrap">
                {fmt(
                  details
                    .filter((d: any) => selectedDetailIds.includes(d.id))
                    .reduce((sum: number, d: any) => sum + (Number(d.quantity || 0) * getEffectivePrice(d)), 0),
                  q.currency
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateOrderDialog(false)}
              >
                Huỷ
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                disabled={selectedDetailIds.length === 0}
                onClick={() => {
                  setShowCreateOrderDialog(false);
                  navigate({
                    to: "/orders/create",
                    search: {
                      quotationId: q.id,
                      customerId: q.customerId,
                      customerName:
                        q.customer?.name || q.customer?.shortName || "",
                      selectedDetailIds: selectedDetailIds.join(","),
                    } as any,
                  });
                }}
              >
                Tiếp tục & Lập Đơn
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL TÍNH GIÁ KỸ THUẬT ĐA THÀNH PHẦN (CHỐNG TÍNH GIÁ ẢO) */}
      {estimatingDetailId && (
        <QuotationEstimatingModal
          isOpen={!!estimatingDetailId}
          onClose={() => setEstimatingDetailId(null)}
          quotationDetailId={estimatingDetailId}
          orderQty={estimatingOrderQty}
          productName={estimatingProductName}
          specs={estimatingSpecs}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] });
          }}
        />
      )}
    </>
  );
}
